import { BRAND, esc, page, traceLine } from './base.js'

/**
 * ตรวจสุขภาพ Tooth Taxi (toothtaxi_checkup) — HTML replacement for the Google
 * Docs form. Unlike every other document this is a CLINICAL FORM filled in by
 * hand on the bus: the system prints the patient / dentist / date block, the
 * dentist ticks and writes the rest with a pen. So it is dense, black-on-white
 * and one page, with real tick boxes and blank fill lines.
 *
 * Input is buildReferralPayload() output (the dialog has no inputs for it).
 *
 * The letterhead is Tooth Taxi's own, not the Smile Seasons wordmark. Its
 * licence / SSO / NHSO numbers and address are copied verbatim from the
 * Google Docs template — they are the Tooth Taxi unit's registrations, which
 * differ from the branch record (Ram's template is the source; never edit
 * them from memory).
 */

const UNIT = {
  title: 'รถทันตกรรมเคลื่อนที่ Tooth Taxi',
  sub: 'Dental Service On the GO! · คลินิกทันตกรรมสไมล์ซีซันส์ สาขาอารีย์',
  regs: [
    ['ใบอนุญาตประกอบกิจการสถานพยาบาล', '10103011467'],
    ['รหัสหน่วยบริการประกันสังคม', '6609975'],
    ['รหัสหน่วยบริการหลักประกันสุขภาพแห่งชาติ', '54104'],
  ],
  address: '388 ชั้น 9 อาคาร เอส.พี. ถนนพหลโยธิน แขวงสามเสนใน เขตพญาไท กทม. 10400',
}

const COMPLAINTS = ['ตรวจสุขภาพช่องปาก', 'แปรงฟันแล้วมีเลือดออก', 'ต้องการขูดหินปูน', 'รู้สึกมีฟันผุ', 'เสียวฟัน', 'ปวดฟัน', 'ฟันโยก']

// [label, has an "on ____" fill]
const DIAGNOSIS = [
  ['Gingivitis'], ['Generalized Chronic Periodontitis'], ['Localized Chronic Periodontitis', 1],
  ['Deep Pit & Fissure', 1], ['Dental Caries', 1], ['Cervical Abrasion (NCCL)', 1],
  ['Reversible Pulpitis', 1], ['Irreversible Pulpitis', 1], ['Pulp Necrosis', 1],
  ['Retained Root (RR)', 1], ['Periodontally Hopeless Tooth', 1], ['Impaction / Embedded Tooth', 1],
  ['Prolonged Retention', 1], ['Other', 0, ''],
]
const TREATMENT = [
  ['Oral Examination & Consultation'], ['X-ray (PA / BW)', 1], ['Scaling FM., Polishing, OHI'],
  ['Airflow'], ['Polishing & Fluoride application', 1, 'with'], ['Sealant', 1],
  ['Filling w/ Composite Resin', 1, 'on', 'base'],
  ['Topical Anesthesia', 1], ['Local Anesthesia injection', 1, 'with'], ['Simple Extraction', 1],
  ['Surgical Extraction', 1], ['Suture', 1, 'with'], ['Other', 0, ''],
]

const cb = (label) => `<span class="cb"><i></i>${label}</span>`
const fill = (w) => `<span class="fill"${w ? ` style="width:${w}"` : ''}></span>`

function item([label, on, word = 'on', extra]) {
  const tail = on ? `<span class="on">${word}</span>${fill()}` : (word === '' ? fill() : '')
  return `<li>${cb(label)}${tail}${extra === 'base'
    ? `</li><li class="sub">(Base w/${fill('22mm')} / Subbase w/${fill('22mm')})`
    : ''}</li>`
}

// Upper: permanent 18…11 | 21…28 with primary 55…51 | 61…65 beneath.
// Lower: primary 85…81 | 71…75 above permanent 48…41 | 31…38.
function toothChart() {
  const UR = [18, 17, 16, 15, 14, 13, 12, 11], UL = [21, 22, 23, 24, 25, 26, 27, 28]
  const LR = [48, 47, 46, 45, 44, 43, 42, 41], LL = [31, 32, 33, 34, 35, 36, 37, 38]
  const primary = (n) => {
    const q = Math.floor(n / 10), i = n % 10
    if (i > 5) return ''
    return String({ 1: 5, 2: 6, 3: 7, 4: 8 }[q] * 10 + i)
  }
  // Blank surface (ด้าน) row the dentist writes into; heavier rule at the midline.
  const surf = `<tr class="surf"><th>ด้าน</th>${Array.from({ length: 16 }, (_, i) => `<td class="${i === 7 ? 'mid' : ''}"></td>`).join('')}</tr>`
  const num = (list, side, upper) => list.map((n, i) => {
    const p = primary(n)
    const main = `<b>${n}</b>`, kid = p ? `<span>${p}</span>` : ''
    return `<td class="${i === 7 && side === 'r' ? 'mid' : ''}">${upper ? main + kid : kid + main}</td>`
  }).join('')
  return `
  <table class="chart">
    ${surf}
    <tr class="num upper"><th></th>${num(UR, 'r', true)}${num(UL, 'l', true)}</tr>
    <tr class="num lower"><th></th>${num(LR, 'r', false)}${num(LL, 'l', false)}</tr>
    ${surf}
  </table>`
}

export function renderToothtaxiCheckup(d, { assets = {} } = {}) {
  const patient = `${d.patient_prefix || d.paitent_prefix || ''}${d.patient_fullname || d.fullname || ''}`
  const doctor = `${d.doctor_prefix || ''}${d.doctor_fullname || ''}`.trim()
  const branch = d.branch_name || 'อารีย์'

  const body = `
  <header class="tt-top">
    ${assets.toothtaxiLogo ? `<img class="tt-logo" src="${esc(assets.toothtaxiLogo)}" alt="Tooth Taxi">` : ''}
    <div>
      <h1>${UNIT.title}</h1>
      <div class="tt-sub">${UNIT.sub}</div>
      <div class="tt-regs">${UNIT.regs.map(([k, v]) => `<span>${k} <b class="num">${v}</b></span>`).join('')}</div>
      <div class="tt-regs">${UNIT.address}</div>
    </div>
  </header>

  <table class="info">
    <tr>
      <td class="w-name"><span>ชื่อ นามสกุล</span><b>${esc(patient)}</b></td>
      <td><span>อายุ</span><b>${d.patient_age ? `${esc(d.patient_age)} ปี` : '&nbsp;'}</b></td>
      <td><span>HN</span><b class="num">${esc(d.HN)}</b></td>
      <td class="w-doc"><span>ทันตแพทย์ผู้รักษา</span><b>${esc(doctor)}${d.doctor_license ? ` <i>ท. ${esc(d.doctor_license)}</i>` : ''}</b></td>
      <td><span>วันที่รักษา</span><b>${esc(d.date_treatment || '')}</b></td>
    </tr>
    <tr>
      <td colspan="3"><span>แพ้ยา</span><b>${esc(d.patient_allergy || '-')}</b></td>
      <td colspan="2"><span>โรคประจำตัว</span><b>${esc(d.patient_disease || '-')}</b></td>
    </tr>
  </table>

  <h2>Chief Complaint</h2>
  <div class="cc">${COMPLAINTS.map(cb).join('')}</div>
  <div class="cc other">${cb('อื่นๆ')}${fill()}</div>

  <section class="dx-tx">
    <div><h2>Diagnosis</h2><ul>${DIAGNOSIS.map(item).join('')}</ul></div>
    <div><h2>Treatment</h2><ul>${TREATMENT.map(item).join('')}</ul></div>
  </section>

  <h2>รายละเอียดการตรวจสุขภาพช่องปาก</h2>
  <div class="cc">${cb('เหงือกอักเสบ')}<span class="cc-fill">${cb('ฟันคุด')}${fill('48mm')}</span>${cb('โรคปริทันต์อักเสบ')}${cb('ฟันผุ')}</div>
  ${toothChart()}
  <p class="note">*กรุณาวงกลมระบุซี่ฟัน หากเป็น Primary Dentition หรือ Mixed Dentition</p>

  <section class="signs">
    <div><div class="space"></div><b>${esc(patient)}</b><span>ผู้รับการรักษา</span></div>
    <div><div class="space"></div><b>${esc(doctor)}</b><span>ทันตแพทย์ผู้รักษา</span></div>
  </section>`

  return page({
    title: `ตรวจสุขภาพ Tooth Taxi ${d.ref_num || d.HN || ''}`.trim(),
    lang: 'th',
    assets,
    footer: `คลินิกทันตกรรมสไมล์ซีซันส์ สาขา${branch}`,
    meta: traceLine(d, 'th'),
    body,
    css: CSS,
  })
}

const LINE = '#334155'
const CSS = `
body { font-size: 9pt; line-height: 1.4; }
.tt-top { display: flex; align-items: center; gap: 5mm; margin-bottom: 3.5mm; }
.tt-logo { width: 30mm; height: auto; flex: none; }
.tt-top h1 { margin: 0; font-size: 16pt; line-height: 1.25; color: ${BRAND.ink}; }
.tt-sub { font-size: 10pt; color: #334155; margin-bottom: .6mm; }
.tt-regs { font-size: 8pt; color: ${BRAND.muted}; display: flex; flex-wrap: wrap; gap: 0 3mm; }
.tt-regs b { color: ${BRAND.ink}; font-weight: 600; }

table.info { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
table.info td { border: 1px solid ${LINE}; padding: 1mm 2mm 1.2mm; vertical-align: top; }
table.info span { display: block; font-size: 7.5pt; color: ${BRAND.muted}; }
table.info b { font-weight: 600; font-size: 9.5pt; }
table.info b i { font-style: normal; font-weight: 400; color: ${BRAND.muted}; font-size: 8.5pt; }
.w-name { width: 30%; } .w-doc { width: 30%; }

h2 { margin: 2.2mm 0 1mm; font-family: 'Plex Thai', sans-serif; font-weight: 600; font-size: 10pt; color: ${BRAND.ink}; }
.cb { display: inline-flex; align-items: center; gap: 1.3mm; white-space: nowrap; }
.cb > i { width: 3mm; height: 3mm; border: 1px solid ${LINE}; border-radius: .5mm; flex: none; }
.fill { display: inline-block; flex: 1; min-width: 12mm; height: 3.6mm; border-bottom: 1px dotted #64748b; margin-left: 1mm; }
.cc { display: flex; flex-wrap: wrap; align-items: center; gap: 1mm 2.6mm; font-size: 8.5pt; }
.cc.other { margin-top: 1mm; flex-wrap: nowrap; }
.cc-fill { display: inline-flex; align-items: flex-end; }

.dx-tx { display: grid; grid-template-columns: 1fr 1fr; gap: 0 8mm; margin-top: 1mm; }
.dx-tx ul { list-style: none; margin: 0; padding: 0; }
.dx-tx li { display: flex; align-items: flex-end; margin-bottom: .9mm; }
.dx-tx li.sub { padding-left: 4.3mm; color: #334155; }
.dx-tx .on { margin-left: 1.2mm; color: ${BRAND.muted}; }

table.chart { width: 100%; border-collapse: collapse; margin-top: 1.5mm; table-layout: fixed; }
table.chart th { width: 9mm; font-weight: 400; font-size: 8pt; color: ${BRAND.muted}; text-align: right; padding-right: 1.5mm; }
table.chart td { border: 1px solid ${LINE}; text-align: center; padding: 0; }
table.chart td.mid { border-right-width: 2px; }
table.chart tr.surf td { height: 6mm; }
table.chart tr.num td { height: 10mm; vertical-align: middle; }
table.chart tr.num b { display: block; font-weight: 600; font-size: 9.5pt; }
table.chart tr.num span { display: block; font-size: 8pt; color: ${BRAND.muted}; }
table.chart tr.upper td { border-bottom-width: 2px; }
.note { margin: 1mm 0 0 9mm; font-size: 8pt; font-style: italic; color: ${BRAND.muted}; }

.signs { display: grid; grid-template-columns: 1fr 1fr; margin-top: 3mm; border: 1px solid ${LINE}; break-inside: avoid; }
.signs > div { padding: 2mm 3mm 2mm; display: flex; flex-direction: column; align-items: center; text-align: center; }
.signs > div + div { border-left: 1px solid ${LINE}; }
.signs .space { height: 13mm; width: 85%; border-bottom: 1px dotted #94a3b8; margin-bottom: 1.5mm; }
.signs b { font-weight: 600; }
.signs span { font-size: 8pt; color: ${BRAND.muted}; }
`
